// Central Chart.js defaults — the single source of truth for chart typography.
// Chart.js draws to <canvas> and cannot inherit CSS, so fonts must be set on
// the global ChartJS.defaults. Imported once for its side effect from
// src/index.js. Do not set font.family on individual charts.
//
// Role-based rule (matches the Analytics Overview plots):
//   - MONO  -> numeric / tabular text: axis tick values and tooltips. The
//             monospaced digits align and read as measured data.
//   - SANS  -> prose: legend labels, axis titles, and the chart title.
import { Chart as ChartJS, Legend, Title, Tooltip } from 'chart.js';

// Register Legend/Title so their default objects exist for the assignments
// below regardless of which controllers a given chart registers. Registration
// is idempotent and only activates a legend/title where a chart configures one
// (charts that want neither set `display: false`), so this shows nothing new.
ChartJS.register(Legend, Title);

// JetBrains Mono / Inter stacks, mirroring Tailwind's `font-mono` / `font-sans`.
const MONO =
  '"JetBrains Mono Variable", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const SANS =
  "'Inter Variable', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";

// Base font (ticks, tooltips, and anything not overridden below) -> mono.
ChartJS.defaults.font.family = MONO;

// Prose roles -> sans. Merge-assign so an undefined `font` default doesn't
// throw and any other font props (size/weight) are preserved.
ChartJS.defaults.plugins.legend.labels.font = {
  ...ChartJS.defaults.plugins.legend.labels.font,
  family: SANS, // legend entries
};
ChartJS.defaults.plugins.title.font = {
  ...ChartJS.defaults.plugins.title.font,
  family: SANS, // chart title
};
ChartJS.defaults.scale.title.font = {
  ...ChartJS.defaults.scale.title.font,
  family: SANS, // axis titles
};

// 'cursorAway' positioner: pin the tooltip to the opposite half of the chart
// area from the cursor, so a tall multi-row readout never covers the lines
// being hovered (which are also click targets for the spotlight). Opt in per
// chart with `position: 'cursorAway'`.
Tooltip.positioners.cursorAway = function cursorAway(elements, eventPosition) {
  const area = this.chart.chartArea;
  if (!area || !eventPosition) return false;
  const onLeft = eventPosition.x < (area.left + area.right) / 2;
  return {
    x: onLeft ? area.right - 8 : area.left + 8,
    y: area.top + 8,
    xAlign: onLeft ? 'right' : 'left',
    yAlign: 'top',
  };
};

// Shared tooltip styling for Chart.js charts. Dark-mode aware, so it cannot
// live on the static ChartJS.defaults - spread it into options.plugins.tooltip
// at render time and keep chart-specific `callbacks`/`filter` alongside it.
export function getChartTooltipStyle(effectiveDark) {
  return {
    backgroundColor: effectiveDark ? '#0b1220' : '#ffffff',
    titleColor: effectiveDark ? '#f3f4f6' : '#111827',
    bodyColor: effectiveDark ? '#e5e7eb' : '#374151',
    // Chart.js defaults footerColor to white: invisible on the light tooltip.
    footerColor: effectiveDark ? '#9ca3af' : '#6b7280',
    footerFont: { weight: 'normal' },
    borderColor: effectiveDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1,
    cornerRadius: 6,
    padding: 8,
  };
}
